import type { TokenRefreshAttemptResult } from './tabCoordination';
import {
  attachTokenRequestDiagnostics,
  createTokenRequestDiagnosticSummary
} from './tokenResponseDiagnostics';

export const createDestroyedRefreshResult = (): TokenRefreshAttemptResult => ({
  success: false,
  error: 'Tab coordination destroyed before coordinated token refresh completed',
  terminalFailure: false
});

export const createDifferentAuthUserRefreshResult = (): TokenRefreshAttemptResult => ({
  success: false,
  error: 'Leader tab is authenticated as a different user',
  terminalFailure: false,
  refreshLocally: true
});

export const normalizeTokenRefreshAttemptResult = (
  result: boolean | TokenRefreshAttemptResult
): TokenRefreshAttemptResult => {
  if (typeof result === 'boolean') {
    return { success: result, terminalFailure: false };
  }

  return result;
};

type RefreshRequestError = Error & {
  code?: string;
  response?: { status?: number };
};

const createRefreshRequestError = (
  refreshResult: TokenRefreshAttemptResult,
  fallbackMessage: string
): RefreshRequestError => {
  const requestError: RefreshRequestError = new Error(refreshResult.error || fallbackMessage);

  if (refreshResult.status) {
    requestError.response = { status: refreshResult.status };
  }

  const diagnosticSummary = createTokenRequestDiagnosticSummary(refreshResult.diagnosticSummary);
  if (diagnosticSummary) {
    attachTokenRequestDiagnostics(requestError, diagnosticSummary);
  }

  return requestError;
};

export const createTerminalRefreshError = (
  refreshResult: TokenRefreshAttemptResult,
  fallbackMessage = 'Chat token refresh failed'
): Error => {
  const requestError = createRefreshRequestError(refreshResult, fallbackMessage);
  requestError.name = 'TerminalChatAuthError';
  requestError.code = 'CHAT_TERMINAL_REFRESH_FAILURE';

  return requestError;
};

type ReportableRefreshError = Error & { reportedAsTokenRefreshFailure?: boolean };

export const markTokenRefreshFailureReported = (error: unknown): void => {
  if (error && typeof error === 'object') {
    (error as ReportableRefreshError).reportedAsTokenRefreshFailure = true;
  }
};

export const wasTokenRefreshFailureReported = (error: unknown): boolean =>
  Boolean(
    error &&
    typeof error === 'object' &&
    (error as ReportableRefreshError).reportedAsTokenRefreshFailure
  );

export const createRefreshFailureError = (
  refreshResult: TokenRefreshAttemptResult,
  fallbackMessage = 'Token refresh failed'
): Error => {
  if (refreshResult.terminalFailure) {
    return createTerminalRefreshError(refreshResult, fallbackMessage);
  }

  return createRefreshRequestError(refreshResult, fallbackMessage);
};
